/**
 * Chart of Accounts Page
 * Manage the account structure for financial tracking
 */

import React, { useState, useEffect } from 'react'
import {
  BookOpen,
  Plus,
  Edit2,
  Trash2,
  FolderTree,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Building,
  FileText,
  XCircle,
  CheckCircle2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { AdminUser } from '@/lib/authService'
import { ChartOfAccountsService } from '@/services/financeService'
import type { ChartOfAccount } from '@/services/financeService'
import { formatCurrency } from '@/lib/currency'

interface ChartOfAccountsProps {
  user: AdminUser | null
}

const accountTypeLabels = {
  asset: 'Asset',
  liability: 'Liability',
  equity: 'Equity',
  revenue: 'Revenue',
  expense: 'Expense',
}

const accountSubtypeLabels = {
  // Assets
  current_asset: 'Current Asset',
  fixed_asset: 'Fixed Asset',
  other_asset: 'Other Asset',
  // Liabilities
  current_liability: 'Current Liability',
  long_term_liability: 'Long-term Liability',
  other_liability: 'Other Liability',
  // Equity
  unrestricted_net_assets: 'Unrestricted Net Assets',
  temporarily_restricted: 'Temporarily Restricted',
  permanently_restricted: 'Permanently Restricted',
  retained_earnings: 'Retained Earnings',
  // Revenue
  contribution: 'Contributions',
  grant: 'Grants',
  program_revenue: 'Program Revenue',
  investment_income: 'Investment Income',
  other_income: 'Other Income',
  // Expenses
  program_expense: 'Program Expenses',
  administrative_expense: 'Administrative Expenses',
  fundraising_expense: 'Fundraising Expenses',
  other_expense: 'Other Expenses',
}

export default function ChartOfAccounts({ user }: ChartOfAccountsProps) {
  const [accounts, setAccounts] = useState<ChartOfAccount[]>([])
  const [loading, setLoading] = useState(true)
  const [showDialog, setShowDialog] = useState(false)
  const [editingAccount, setEditingAccount] = useState<ChartOfAccount | null>(null)
  const [filterType, setFilterType] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState('')

  const [formData, setFormData] = useState({
    account_code: '',
    account_name: '',
    account_type: 'asset' as any,
    account_subtype: 'current_asset' as any,
    parent_account_id: '',
    description: '',
    is_active: true,
  })

  useEffect(() => {
    loadAccounts()
  }, [])

  useEffect(() => {
    // Auto-initialize standard accounts if none exist
    if (accounts.length === 0 && !loading && user?.churchId) {
      autoInitializeAccounts()
    }
  }, [accounts, loading])

  async function loadAccounts() {
    if (!user?.churchId) return

    setLoading(true)
    try {
      const data = await ChartOfAccountsService.getAccounts(user.churchId, {
        includeInactive: true,
      })
      setAccounts(data)
    } catch (error) {
      console.error('Error loading accounts:', error)
    } finally {
      setLoading(false)
    }
  }

  function openAddDialog() {
    setEditingAccount(null)
    setFormData({
      account_code: '',
      account_name: '',
      account_type: 'asset',
      account_subtype: 'current_asset',
      parent_account_id: '',
      description: '',
      is_active: true,
    })
    setShowDialog(true)
  }

  function openEditDialog(account: ChartOfAccount) {
    setEditingAccount(account)
    setFormData({
      account_code: account.account_code,
      account_name: account.account_name,
      account_type: account.account_type,
      account_subtype: account.account_subtype,
      parent_account_id: account.parent_account_id || '',
      description: account.description || '',
      is_active: account.is_active,
    })
    setShowDialog(true)
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!user?.churchId) return

    try {
      if (editingAccount) {
        await ChartOfAccountsService.updateAccount(editingAccount.id, formData)
      } else {
        await ChartOfAccountsService.createAccount({
          ...formData,
          church_id: user.churchId,
          current_balance: 0,
        })
      }
      setShowDialog(false)
      loadAccounts()
    } catch (error) {
      console.error('Error saving account:', error)
      alert('Failed to save account')
    }
  }

  async function handleToggleActive(account: ChartOfAccount) {
    const action = account.is_active ? 'deactivate' : 'activate'
    if (!confirm(`Are you sure you want to ${action} this account?`)) return

    try {
      await ChartOfAccountsService.updateAccount(account.id, {
        is_active: !account.is_active
      })
      loadAccounts()
    } catch (error) {
      console.error(`Error ${action}ing account:`, error)
      alert(`Failed to ${action} account`)
    }
  }

  async function handleDelete(accountId: string) {
    if (!confirm('Are you sure you want to permanently delete this account? This cannot be undone.')) return

    try {
      await ChartOfAccountsService.deleteAccount(accountId)
      loadAccounts()
    } catch (error) {
      console.error('Error deleting account:', error)
      alert('Failed to delete account. Accounts with transactions cannot be deleted.')
    }
  }

  async function autoInitializeAccounts() {
    if (!user?.churchId) return

    try {
      console.log('Auto-initializing standard chart of accounts...')
      await ChartOfAccountsService.initializeStandardChartOfAccounts(user.churchId)
      loadAccounts()
    } catch (error) {
      console.error('Error auto-initializing accounts:', error)
      // Fail silently - user can manually initialize if needed
    }
  }

  async function initializeStandardAccounts() {
    if (!user?.churchId) return
    if (!confirm('This will create standard accounts. Any existing accounts will remain. Continue?')) return

    try {
      await ChartOfAccountsService.initializeStandardChartOfAccounts(user.churchId)
      loadAccounts()
      alert('Standard chart of accounts created successfully!')
    } catch (error) {
      console.error('Error initializing accounts:', error)
      alert('Failed to initialize accounts')
    }
  }

  const filteredAccounts = accounts
    .filter((acc) => filterType === 'all' || acc.account_type === filterType)
    .filter((acc) =>
      searchTerm === '' ||
      acc.account_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      acc.account_name.toLowerCase().includes(searchTerm.toLowerCase())
    )

  function getAccountTypeIcon(type: string) {
    switch (type) {
      case 'asset':
        return <Building className="h-4 w-4" />
      case 'liability':
        return <TrendingDown className="h-4 w-4" />
      case 'equity':
        return <DollarSign className="h-4 w-4" />
      case 'revenue':
        return <TrendingUp className="h-4 w-4" />
      case 'expense':
        return <FileText className="h-4 w-4" />
      default:
        return <BookOpen className="h-4 w-4" />
    }
  }

  function getAccountTypeBadge(type: string) {
    const variants: any = {
      asset: 'default',
      liability: 'secondary',
      equity: 'outline',
      revenue: 'default',
      expense: 'secondary',
    }
    return (
      <Badge variant={variants[type] || 'outline'} className="gap-1">
        {getAccountTypeIcon(type)}
        {accountTypeLabels[type as keyof typeof accountTypeLabels]}
      </Badge>
    )
  }

  const subtypesByType: Record<string, string[]> = {
    asset: ['current_asset', 'fixed_asset', 'other_asset'],
    liability: ['current_liability', 'long_term_liability', 'other_liability'],
    equity: ['unrestricted_net_assets', 'temporarily_restricted', 'permanently_restricted', 'retained_earnings'],
    revenue: ['contribution', 'grant', 'program_revenue', 'investment_income', 'other_income'],
    expense: ['program_expense', 'administrative_expense', 'fundraising_expense', 'other_expense'],
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <h1 className="text-3xl font-bold tracking-tight">Chart of Accounts</h1>
        <p className="text-muted-foreground">
          Standard accounts are created automatically. Deactivate accounts you don't need.
        </p>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex gap-2">
          <Button onClick={openAddDialog}>
            <Plus className="mr-2 h-4 w-4" />
            Add Account
          </Button>
          <Button variant="outline" onClick={initializeStandardAccounts}>
            <FolderTree className="mr-2 h-4 w-4" />
            Initialize Standard Accounts
          </Button>
        </div>

        <div className="flex gap-2">
          <Input
            placeholder="Search accounts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-[250px]"
          />
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="asset">Assets</SelectItem>
              <SelectItem value="liability">Liabilities</SelectItem>
              <SelectItem value="equity">Equity</SelectItem>
              <SelectItem value="revenue">Revenue</SelectItem>
              <SelectItem value="expense">Expenses</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Accounts Table */}
      <Card>
        <CardHeader>
          <CardTitle>Accounts ({filteredAccounts.length})</CardTitle>
          <CardDescription>
            {accounts.length === 0
              ? 'Standard accounts will be created automatically on first use.'
              : 'Deactivate accounts you don\'t need. Edit to customize names and codes.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading accounts...</p>
              </div>
            </div>
          ) : filteredAccounts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchTerm || filterType !== 'all' ? 'No accounts match your filters.' : 'No accounts found.'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Account Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Subtype</TableHead>
                  <TableHead className="text-right">Current Balance</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAccounts.map((account) => (
                  <TableRow key={account.id}>
                    <TableCell className="font-mono font-medium">
                      {account.account_code}
                    </TableCell>
                    <TableCell>{account.account_name}</TableCell>
                    <TableCell>{getAccountTypeBadge(account.account_type)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {accountSubtypeLabels[account.account_subtype as keyof typeof accountSubtypeLabels]}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(account.current_balance, 'GHS')}
                    </TableCell>
                    <TableCell>
                      {account.is_active ? (
                        <Badge variant="default">Active</Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(account)}
                          title="Edit account"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant={account.is_active ? "ghost" : "default"}
                          size="sm"
                          onClick={() => handleToggleActive(account)}
                          title={account.is_active ? "Deactivate account" : "Activate account"}
                        >
                          {account.is_active ? (
                            <XCircle className="h-4 w-4" />
                          ) : (
                            <CheckCircle2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingAccount ? 'Edit Account' : 'Add New Account'}
            </DialogTitle>
            <DialogDescription>
              {editingAccount
                ? 'Update the account details below.'
                : 'Fill in the details to create a new account.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              {/* Account Code */}
              <div className="space-y-2">
                <Label htmlFor="account_code">
                  Account Code <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="account_code"
                  value={formData.account_code}
                  onChange={(e) =>
                    setFormData({ ...formData, account_code: e.target.value })
                  }
                  placeholder="e.g., 1000"
                  required
                />
              </div>

              {/* Account Name */}
              <div className="space-y-2">
                <Label htmlFor="account_name">
                  Account Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="account_name"
                  value={formData.account_name}
                  onChange={(e) =>
                    setFormData({ ...formData, account_name: e.target.value })
                  }
                  placeholder="e.g., Cash in Bank"
                  required
                />
              </div>

              {/* Account Type */}
              <div className="space-y-2">
                <Label htmlFor="account_type">
                  Account Type <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.account_type}
                  onValueChange={(value) =>
                    setFormData({
                      ...formData,
                      account_type: value as any,
                      account_subtype: subtypesByType[value][0] as any,
                    })
                  }
                >
                  <SelectTrigger id="account_type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="asset">Asset</SelectItem>
                    <SelectItem value="liability">Liability</SelectItem>
                    <SelectItem value="equity">Equity</SelectItem>
                    <SelectItem value="revenue">Revenue</SelectItem>
                    <SelectItem value="expense">Expense</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Account Subtype */}
              <div className="space-y-2">
                <Label htmlFor="account_subtype">
                  Account Subtype <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.account_subtype}
                  onValueChange={(value) =>
                    setFormData({ ...formData, account_subtype: value as any })
                  }
                >
                  <SelectTrigger id="account_subtype">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {subtypesByType[formData.account_type].map((subtype) => (
                      <SelectItem key={subtype} value={subtype}>
                        {accountSubtypeLabels[subtype as keyof typeof accountSubtypeLabels]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Optional description"
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDialog(false)}
              >
                Cancel
              </Button>
              <Button type="submit">
                {editingAccount ? 'Update Account' : 'Add Account'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
